import type { CleaningManagementRecord } from "./cleaning-workspace";

type MemoRecord = Pick<CleaningManagementRecord, "id" | "receiptNumber" | "memo">;

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;").replaceAll("'", "&#39;");
}

export function cleaningMemoDialogHtml(record: MemoRecord, editing: boolean): string {
  return `<form class="cleaning-memo-form">
    <header class="cleaning-memo-head"><div><p>${escapeHtml(record.receiptNumber)}</p>
      <h2 id="cleaning-memo-title">${editing ? "メモを入力" : "メモ全文"}</h2></div>
      <button type="button" class="dialog-close" data-memo-close aria-label="閉じる">×</button></header>
    <div class="cleaning-memo-body">
      ${editing ? `<label for="cleaning-memo-input">メモ</label><textarea id="cleaning-memo-input" name="memo" rows="8" maxlength="5000" placeholder="確認事項や引き継ぎ内容などを入力してください。">${escapeHtml(record.memo)}</textarea><small>5000文字まで入力できます。</small>`
        : `<div class="cleaning-memo-full">${escapeHtml(record.memo || "－")}</div>`}
      <p class="cleaning-memo-error" role="alert" hidden></p>
    </div>
    <footer class="cleaning-memo-actions"><button type="button" class="button secondary" data-memo-close>${editing ? "取消" : "閉じる"}</button>
      ${editing ? '<button type="submit" class="button cleaning-memo-save">保存</button>' : '<button type="button" class="button cleaning-memo-save" data-memo-edit>編集</button>'}</footer>
  </form>`;
}

export function openCleaningMemoDialog(record: MemoRecord, editing: boolean, options: {
  showModal: (dialog: HTMLDialogElement) => void;
  save: (input: { id: string; memo: string; expectedMemo: string }) => Promise<{ id: string; memo: string }>;
  onSaved: (result: { id: string; memo: string }) => void;
}): void {
  if (document.querySelector(".cleaning-memo-dialog[open]")) return;
  const dialog = document.createElement("dialog");
  dialog.className = "cleaning-memo-dialog";
  dialog.setAttribute("aria-labelledby", "cleaning-memo-title");
  // Keep outside the app's render root so unrelated refreshes cannot erase unsaved input.
  document.body.appendChild(dialog);
  let saving = false;
  const close = (): void => { if (!saving) dialog.close(); };
  dialog.addEventListener("cancel", (event) => { if (saving) event.preventDefault(); });
  dialog.addEventListener("close", () => dialog.remove(), { once: true });

  const draw = (edit: boolean): void => {
    dialog.innerHTML = cleaningMemoDialogHtml(record, edit);
    dialog.querySelectorAll<HTMLButtonElement>("[data-memo-close]").forEach((button) => button.addEventListener("click", close));
    dialog.querySelector<HTMLButtonElement>("[data-memo-edit]")?.addEventListener("click", () => draw(true));
    const form = dialog.querySelector<HTMLFormElement>("form")!;
    const textarea = form.querySelector<HTMLTextAreaElement>("textarea");
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!textarea || saving || !form.reportValidity()) return;
      void saveMemo(form, textarea);
    });
    textarea?.focus();
  };
  const saveMemo = async (form: HTMLFormElement, textarea: HTMLTextAreaElement): Promise<void> => {
    saving = true;
    form.setAttribute("aria-busy", "true");
    const buttons = Array.from(form.querySelectorAll<HTMLButtonElement>("button"));
    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
    const error = form.querySelector<HTMLElement>(".cleaning-memo-error")!;
    error.hidden = true;
    textarea.disabled = true;
    buttons.forEach((button) => { button.disabled = true; });
    submit.textContent = "保存中…";
    try {
      const result = await options.save({ id: record.id, memo: textarea.value, expectedMemo: record.memo });
      dialog.close();
      options.onSaved(result);
    } catch (cause) {
      error.textContent = cause instanceof Error ? cause.message : "メモを保存できませんでした。";
      error.hidden = false;
    } finally {
      saving = false;
      form.removeAttribute("aria-busy");
      textarea.disabled = false;
      buttons.forEach((button) => { button.disabled = false; });
      submit.textContent = "保存";
    }
  };
  draw(editing);
  options.showModal(dialog);
  dialog.querySelector<HTMLTextAreaElement>("textarea")?.focus();
}
